/**
 * Pure helpers for the software-product catalog.
 *
 * Everything here is a plain function over plain data, because the frontend
 * test harness has no jsdom/RTL: the rules that matter (which products a
 * publisher may choose, whether a key is valid, how columns are ordered) are
 * extracted from the components so vitest can pin them directly.
 */
import {
  SOFTWARE_PRODUCT_ORDER,
  resolveProductLabel,
  type SoftwareAudience,
  type SoftwareProduct,
  type SoftwareProductRow,
  type SoftwareRelease,
} from '../../types/downloads';

/** Mirrors the DB CHECK on `software_products.key` and the backend's
 * `_PRODUCT_KEY_RE`. Three copies of one rule, deliberately: this one exists to
 * tell the admin *while typing* rather than after a round trip. */
export const PRODUCT_KEY_PATTERN = /^[a-z0-9_]{1,40}$/;

export const PRODUCT_KEY_HINT =
  'Lowercase letters, digits and underscores only (max 40). It becomes part of the ' +
  'download URL and the S3 path, and can never be changed.';

/** `null` when the key is valid, else why it is not — suitable for a
 * TextField `helperText`. Returns a reason rather than a boolean so the dialog
 * cannot invent its own wording for a rule it does not own. */
export function validateProductKey(key: string): string | null {
  if (!key) return 'A product key is required.';
  if (key.length > 40) return 'Too long — 40 characters max.';
  if (!PRODUCT_KEY_PATTERN.test(key)) return PRODUCT_KEY_HINT;
  return null;
}

/** `label` → a legal key suggestion. Convenience only: the field stays
 * editable and `validateProductKey` is still what decides. */
export function suggestProductKey(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
}

/**
 * The audience choices offered when creating a product, and the copy that
 * explains them.
 *
 * The text lives here rather than inline in the dialog for one reason: it is
 * the only warning a publisher gets before an irreversible decision, and a
 * test can hold this file to naming the consequence. `audience` is immutable
 * once the product exists (DB trigger), so "you can change it later" is false
 * and the copy must never imply it.
 */
export const AUDIENCE_CHOICES: ReadonlyArray<{
  value: SoftwareAudience;
  title: string;
  description: string;
}> = [
  {
    value: 'customer',
    title: 'Customer',
    description: 'Every signed-in user of the dashboard can see and download its releases.',
  },
  {
    value: 'xer_internal',
    title: 'XER internal',
    description:
      'Only XER employees can see it. Customers get the same 404 as for a product that does not exist.',
  },
];

/** Shown until an audience is picked. There is deliberately no default — see
 * `canCreateProduct`. */
export const AUDIENCE_REQUIRED_HINT =
  'Pick an audience — there is no default, because it cannot be changed later.';

/** Shown once `customer` is picked: the choice that can leak a build. */
export const AUDIENCE_CUSTOMER_WARNING =
  'Anything published under this product is visible to every customer. Builds that embed ' +
  'credentials (like the UC Flasher engineer build) belong under an XER-internal product.';

export interface NewProductDraft {
  label: string;
  key: string;
  /** `''` models "the admin has not chosen yet" — the initial state. */
  audience: SoftwareAudience | '';
}

/**
 * May this draft be submitted?
 *
 * Extracted from the dialog so the **no-default-audience rule** is testable:
 * the harness has no jsdom, so a rule left in component state is a rule
 * nothing can check. It is not a formality — audience is immutable once the
 * product exists, so a preselected value would let the most consequential
 * choice in the flow be made by not making it.
 */
export function canCreateProduct(draft: NewProductDraft): boolean {
  return (
    draft.label.trim().length > 0 && validateProductKey(draft.key) === null && draft.audience !== ''
  );
}

export interface ProductOption {
  value: string;
  label: string;
  audience: SoftwareProductRow['audience'];
}

/**
 * The publish dropdown's options: non-archived products, ordered.
 *
 * Archiving is what retires a product, and this is where it takes visible
 * effect — an archived product is not offered. (The backend refuses it too;
 * this only keeps the admin from picking something that would be refused.)
 */
export function buildProductOptions(products: SoftwareProductRow[]): ProductOption[] {
  return products
    .filter((p) => !p.archived)
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order || a.key.localeCompare(b.key))
    .map((p) => ({ value: p.key, label: p.label, audience: p.audience }));
}

/**
 * Keep a selected product valid as the option list loads or changes.
 *
 * Returns the product that should be selected: the current one if it is still
 * offered, else the first option, else `''`. Without this, archiving the
 * selected product (or loading the list after the panel mounted) leaves the
 * form holding a key the dropdown no longer contains — MUI renders that as a
 * blank select that still submits the stale value.
 */
export function reconcileSelectedProduct(selected: string, options: ProductOption[]): string {
  if (options.some((o) => o.value === selected)) return selected;
  return options[0]?.value ?? '';
}

/** The server-side `sort_order` for each product present in a release list.
 * Absent for any release whose embed the server did not send. */
export function productSortOrders(releases: SoftwareRelease[]): Map<string, number> {
  const orders = new Map<string, number>();
  for (const release of releases) {
    const order = release.software_products?.sort_order;
    if (typeof order === 'number' && !orders.has(release.product)) {
      orders.set(release.product, order);
    }
  }
  return orders;
}

/** The label for a product as seen in a release list — server label first. */
export function releaseProductLabel(releases: SoftwareRelease[], product: string): string {
  const withLabel = releases.find(
    (r) => r.product === product && r.software_products?.label !== undefined
  );
  return resolveProductLabel(product, withLabel?.software_products?.label ?? null);
}

/**
 * Column order for /downloads: the server's `sort_order` when present,
 * otherwise the bundled `SOFTWARE_PRODUCT_ORDER`, otherwise alphabetical.
 *
 * Server-ordered products always come first as a group. Mixing the two scales
 * (a `sort_order` of 2 against an index of 2) would let a newly created
 * product silently displace a known one depending on which number happened to
 * be smaller; keeping them as separate tiers makes the ordering explainable.
 */
export function orderProductsFor(products: string[], sortOrders: Map<string, number>): string[] {
  return [...products].sort((a, b) => {
    const sa = sortOrders.get(a);
    const sb = sortOrders.get(b);
    if (sa !== undefined && sb !== undefined) return sa - sb || a.localeCompare(b);
    if (sa !== undefined) return -1;
    if (sb !== undefined) return 1;
    const ia = SOFTWARE_PRODUCT_ORDER.indexOf(a as SoftwareProduct);
    const ib = SOFTWARE_PRODUCT_ORDER.indexOf(b as SoftwareProduct);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return a.localeCompare(b);
  });
}
